import React, { useContext, useEffect, useRef, useState } from 'react';
import { ApiContext } from '@api/api';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import { CompanyLocationsDetailOutDTO, LovDTO, ModulesEnum, Pageable, VisibilityEnum } from '@api/ringil-api.v2';
import COHeadline from '@components/framework/headlines/COHeadline.tsx';
import SectionPanel from '@components/framework/panels/SectionPanel.tsx';
import RingilButton from '@components/ringil3/Buttons/RingilButton.tsx';
import COInlineMessage from '@components/ringil3/COInlineMessage/COInlineMessage.tsx';
import SvgGrayInfo from '@components/ringil3/icons/GrayInfo.tsx';
import RingilDataTable, {
  ColumnConfig,
  FilterConfig,
  FilterType,
  MassActionConfig,
  PagedData,
} from '@components/RingilDataTable/RingilDataTable.tsx';
import ConfirmDialog from '@components/RingilComponents/ConfirmDialog.tsx';
import RingilSidebar from '@components/ringil3/Sidebar/RingilSidebar.tsx';
import LocationSidebarHeader from '@components/RingilComponents/LocationSidebarHeader/LocationSidebarHeader.tsx';
import { MultiSelect } from 'primereact/multiselect';
import { Button } from 'primereact/button';
import EyeSvg from '@icons/EyeSvg.tsx';
import SvgEye from '@icons/eye.svg?react';
import SvgEyeSlash from '@icons/eye-slash.svg?react';
import SvgList from '@icons/list.svg?react';
import SvgTrashBin from '@icons/delete-trash.svg?react';

interface ComponentProps {
  location: CompanyLocationsDetailOutDTO;
  onReload: () => void;
}

interface CompanyFilter {
  companyName?: string;
}

type ConfirmAction = 'SHOW_ALL' | 'HIDE_ALL';

const VisibilitySection: React.FC<ComponentProps> = ({ location, onReload }) => {
  const { tr } = useTranslationLgs();
  const { changeACLForLocations, lovAllCompaniesByInheritedModules } = useContext(ApiContext);

  const [confirmAction, setConfirmAction] = useState<ConfirmAction | null>(null);
  const [sidebarVisible, setSidebarVisible] = useState(false);
  const [companies, setCompanies] = useState<LovDTO[]>([]);
  const [selectedCompanies, setSelectedCompanies] = useState<LovDTO[]>([]);

  const tableRef = useRef<{ reloadData: () => void; selectedObjects: LovDTO[]; clear: () => void }>(null);

  // A deleted location is read-only everywhere — Restore on the detail is the only action offered.
  const readOnly = location.isDeleted;

  const visibility = location.acl?.visibility;
  const visibleToCompanies = location.acl?.visibleToCompanies ?? [];

  useEffect(() => {
    if (sidebarVisible) {
      lovAllCompaniesByInheritedModules({ modules: [ModulesEnum.TIMESLOTS, ModulesEnum.SUPPLIER_NOTIFICATION] }, data => setCompanies(data.filter(company => company.active)));
      setSelectedCompanies(visibleToCompanies);
    }
  }, [sidebarVisible]);

  useEffect(() => {
    tableRef.current?.clear();
    tableRef.current?.reloadData();
  }, [location.acl?.visibleToCompanies]);

  const changeACL = (newVisibility: VisibilityEnum, newVisibleToCompanies: LovDTO[], onDone?: () => void) => {
    changeACLForLocations({ locationIds: [location.id], visibility: newVisibility, visibleToCompanies: newVisibleToCompanies }, () => {
      onDone?.();
      onReload();
    });
  };

  const removeCompanies = (companyIds: string[]) => {
    changeACL(
      VisibilityEnum.VISIBLE_TO_COMPANIES,
      visibleToCompanies.filter(c => !companyIds.includes(c.id)),
      () => tableRef.current?.clear(),
    );
  };

  const loadCompanies = (filter: CompanyFilter, pageable: Pageable, onSuccess: (pagedData: PagedData<LovDTO>) => void) => {
    const name = filter?.companyName?.toLowerCase() ?? '';
    const all = visibleToCompanies.filter(c => !name || c.name?.toLowerCase().includes(name)).sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''));
    const start = (pageable.pageNumber - 1) * pageable.pageSize;
    onSuccess({ data: all.slice(start, start + pageable.pageSize), total: all.length, page: pageable.pageNumber, size: pageable.pageSize });
  };

  const filtersConf: FilterConfig[] = [
    {
      filterType: FilterType.InputText,
      placeholder: tr('LocalityDetail.companyName', 'Company name'),
      value: ['companyName'],
      dataTestId: 'location-visibility-company-filter',
    },
  ];

  const columnsConfig: ColumnConfig<LovDTO>[] = [
    {
      id: 'company',
      field: 'name',
      header: 'LocalityDetail.company',
      headerDefault: 'Company',
      width: '85%',
      sortable: false,
      order: 1,
      render: row => <span className={'text-content-brand'}>{row.name}</span>,
    },
    {
      id: 'action',
      field: 'action',
      width: '15%',
      sortable: false,
      order: 2,
      render: row =>
        readOnly ? null : (
          <div className={'flex justify-end'}>
            <RingilButton
              mode={'table'}
              icon={<SvgTrashBin />}
              onClick={() => removeCompanies([row.id])}
              dataTestId={'location-visibility-remove-company'}
            />
          </div>
        ),
    },
  ];

  const massActionConfig: MassActionConfig<LovDTO>[] = [
    {
      label: tr('LocalityDetail.remove', 'Remove'),
      icon: <SvgTrashBin />,
      onClick: () => {
        const ids = (tableRef.current?.selectedObjects ?? []).map(c => c.id);
        if (ids.length > 0) removeCompanies(ids);
      },
    },
  ];

  const renderAddCompanyLink = (securedId: string) =>
    readOnly ? null : (
    <Button
      label={tr('LocalityDetail.addCompany', 'Add company')}
      icon={'pi pi-plus'}
      className={'p-button-text !text-[#FE5000] hover:!text-orange-600 !inline-flex !items-center'}
      onClick={() => setSidebarVisible(true)}
      data-testid={securedId}
    />
    );

  const renderActionButtons = () =>
    readOnly ? null : (
    <div className={'flex flex-row items-center gap-2'}>
      {visibility !== VisibilityEnum.VISIBLE_TO_COMPANIES && (
        <RingilButton
          mode={'outlined'}
          label={tr('LocalityDetail.showToSelected', 'Show to selected')}
          icon={<SvgList />}
          onClick={() => setSidebarVisible(true)}
          dataTestId={'location-visibility-show-selected'}
        />
      )}
      {visibility !== VisibilityEnum.PUBLIC && (
        <RingilButton
          mode={'outlined'}
          label={tr('LocalityDetail.showToAll', 'Show to all')}
          icon={<SvgEye className={'[&_path]:fill-current'} />}
          onClick={() => setConfirmAction('SHOW_ALL')}
          dataTestId={'location-visibility-show-all'}
        />
      )}
      {visibility !== VisibilityEnum.PRIVATE && (
        <RingilButton
          mode={'outlined'}
          label={tr('LocalityDetail.hideFromAll', 'Hide from all')}
          icon={<SvgEyeSlash className={'[&_path]:fill-current'} />}
          onClick={() => setConfirmAction('HIDE_ALL')}
          dataTestId={'location-visibility-hide-all'}
        />
      )}
    </div>
    );

  const renderSelectedCompanies = () => (
    <div className={'flex flex-col gap-2'}>
      <div className={'flex flex-row items-center gap-2'}>
        <COHeadline variant={'h4'} title={tr('LocalityDetail.visibleToSelected', 'Visible to selected companies')} />
        {renderAddCompanyLink('location-visibility-add-company')}
      </div>
      {visibleToCompanies.length > 0 ? (
        <RingilDataTable
          ref={tableRef}
          columnConfig={columnsConfig as ColumnConfig<unknown>[]}
          filtersConf={filtersConf}
          loadDataFromApi={loadCompanies}
          filterName={'location_detail_visibility_companies'}
          massActionConfig={readOnly ? undefined : (massActionConfig as MassActionConfig<unknown>[])}
        />
      ) : (
        <div className={'flex flex-col'} data-testid={'location-visibility-empty'}>
          <div className={'bg-content-quaternary px-6 py-3 font-medium text-content-primary'}>{tr('LocalityDetail.company', 'Company')}</div>
          <div className={'flex flex-row flex-wrap items-center justify-center gap-1 bg-white py-10'}>
            <span className={'text-content-primary'}>
              {tr('LocalityDetail.noCompaniesSelected', 'You have not yet selected any company that can see this location.')}
            </span>
            {renderAddCompanyLink('location-visibility-add-company-empty')}
          </div>
        </div>
      )}
    </div>
  );

  return (
    <SectionPanel
      icon={<EyeSvg width={24} height={24} fill={'var(--content-tertiary)'} />}
      iconColor={'var(--content-tertiary)'}
      heading={tr('LocalityDetail.visibilityToCompanies', 'Location visibility to companies')}
      headerRight={renderActionButtons()}
      dataTestId={'location-visibility-section'}>
      {visibility === VisibilityEnum.PUBLIC && (
        <COInlineMessage variant={'info'} icon={<SvgGrayInfo />} dataTestId={'location-visibility-message-public'}>
          {tr('LocalityDetail.visibleToAll', 'The location is visible to all companies.')}
        </COInlineMessage>
      )}
      {visibility === VisibilityEnum.PRIVATE && (
        <COInlineMessage variant={'error'} icon={<SvgGrayInfo />} dataTestId={'location-visibility-message-private'}>
          {tr('LocalityDetail.hiddenFromAll', 'The location is hidden from all companies.')}
        </COInlineMessage>
      )}

      {visibility === VisibilityEnum.VISIBLE_TO_COMPANIES && renderSelectedCompanies()}

      <ConfirmDialog
        visible={confirmAction === 'SHOW_ALL'}
        dialogTitle={tr('LocalityDetail.showToAllTitle', 'Make location visible to all')}
        dialogMessage={tr('LocalityDetail.showToAllMessage', 'You will make the location {{locationName}} visible to all companies.', {
          locationName: location.name,
        })}
        onConfirm={() => changeACL(VisibilityEnum.PUBLIC, [], () => setConfirmAction(null))}
        onCancel={() => setConfirmAction(null)}
        onAcceptButtonText={tr('LocalityDetail.yesShowToAll', 'Yes, make visible')}
        onCancelButtonText={tr('generic.cancel', 'Cancel')}
      />
      <ConfirmDialog
        visible={confirmAction === 'HIDE_ALL'}
        dialogTitle={tr('LocalityDetail.hideFromAllTitle', 'Hide location from all')}
        dialogMessage={tr('LocalityDetail.hideFromAllMessage', 'You will hide the location {{locationName}} from all companies.', {
          locationName: location.name,
        })}
        onConfirm={() => changeACL(VisibilityEnum.PRIVATE, [], () => setConfirmAction(null))}
        onCancel={() => setConfirmAction(null)}
        onAcceptButtonText={tr('LocalityDetail.yesHide', 'Yes, hide')}
        onCancelButtonText={tr('generic.cancel', 'Cancel')}
      />

      <RingilSidebar
        visible={sidebarVisible}
        heading={tr('LocalityDetail.visibilityToSelectedCompanies', 'Visibility to selected companies')}
        onContinue={() => changeACL(VisibilityEnum.VISIBLE_TO_COMPANIES, selectedCompanies, () => setSidebarVisible(false))}
        onBack={() => setSidebarVisible(false)}
        disableSaveButton={selectedCompanies.length === 0}
        dataTestIdContinue={'location-visibility-sidebar-save'}
        dataTestIdBack={'location-visibility-sidebar-back'}
      >
        <div className={'flex flex-col gap-6'}>
          <LocationSidebarHeader locationId={location.id} variant={'locality'} />
          <div className={'flex flex-col gap-2'}>
            <label className={'text-content-secondary text-sm'}>{tr('LocalityDetail.addVisibilityForCompanies', 'Add visibility for companies')}</label>
            <MultiSelect
            value={selectedCompanies}
            options={companies}
            optionLabel={'name'}
            dataKey={'id'}
            filter={true}
            onChange={e => setSelectedCompanies(e.value)}
            placeholder={tr('InputACL.selectPartnerOrPartners', 'Select partner or partners')}
            className={'w-full'}
            data-testid={'location-visibility-company-multiselect'}
          />
          </div>
        </div>
      </RingilSidebar>
    </SectionPanel>
  );
};

export default VisibilitySection;
